"use client";

import { useTranslations } from "next-intl";
import { SectionEyebrow, SectionTitle } from "../section-eyebrow";
import { ScrollReveal, ScrollStagger, StaggerItem } from "../scroll-reveal";

type Group = { label: string; tags: string[] };

export function Capabilities() {
  const t = useTranslations("capabilities");
  const groups = t.raw("groups") as Group[];

  return (
    <section className="relative py-12 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ScrollReveal as="header" className="mb-10 flex flex-col gap-5 sm:max-w-2xl lg:mb-14">
          <div className="flex items-center justify-between">
            <SectionEyebrow>{t("eyebrow")}</SectionEyebrow>
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 sm:inline">
              / stack 2026
            </span>
          </div>
          <SectionTitle text={t("title")} italicLastWords={2} />
          <p data-blend className="text-base sm:text-lg">
            {t("subtitle")}
          </p>
        </ScrollReveal>

        <ScrollStagger
          className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl bg-border ring-1 ring-border md:grid-cols-2 lg:grid-cols-3"
          staggerChildren={0.06}
        >
          {groups.map((group, gi) => (
            <StaggerItem
              key={group.label}
              as="article"
              className="flex flex-col gap-5 bg-card p-6 transition-colors hover:bg-muted/40 sm:p-7"
            >
              <div className="flex items-baseline justify-between">
                <h3 className="font-sans text-base font-medium tracking-tight text-foreground">
                  {group.label}
                </h3>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80">
                  / {String(gi + 1).padStart(2, "0")}
                </span>
              </div>
              <span aria-hidden className="hairline" />
              <ul className="flex flex-wrap gap-2">
                {group.tags.map((tag) => (
                  <li
                    key={tag}
                    className="inline-flex items-center rounded-full border border-border bg-background px-2.5 py-1 font-mono text-[11px] text-foreground/85 transition-all hover:border-aurora-cyan/40 hover:bg-aurora-cyan/5 hover:text-foreground"
                  >
                    {tag}
                  </li>
                ))}
              </ul>
            </StaggerItem>
          ))}
        </ScrollStagger>
      </div>
    </section>
  );
}
